import { memo, useEffect, useRef, useState } from "react";
import { cellCenterMM, CSS_FONT, fitFontPx } from "@/lib/pinLayout";
import { getProjectThumbnailUrl } from "@/lib/imageCache";

function MiniImage({ cell, projectId }) {
  const containerRef = useRef(null);
  const [src, setSrc] = useState(cell.imageData || null);

  useEffect(() => {
    let cancelled = false;
    let observer;

    setSrc(cell.imageData || null);

    if (cell.imageData || !cell.imageId || !projectId) {
      return () => {
        cancelled = true;
      };
    }

    const loadImage = () => {
      getProjectThumbnailUrl(projectId, cell.imageId)
        .then((url) => {
          if (!cancelled) setSrc(url);
        })
        .catch((error) => {
          console.error("[MiniPage] Gagal memuat gambar:", error);
        });
    };

    const element = containerRef.current;

    if ("IntersectionObserver" in window && element) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadImage();
        }
      }, {
        rootMargin: "150px",
      });

      observer.observe(element);
    } else {
      loadImage();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [cell.imageData, cell.imageId, projectId]);

  return (
    <div ref={containerRef} className="h-full w-full">
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transformOrigin: "center",
            transform: `translate(${(cell.offX || 0) * 100}%, ${(cell.offY || 0) * 100}%) scale(${cell.zoom || 1})`,
          }}
        />
      )}
    </div>
  );
}

function MiniPageImpl({
  page, layout, projectId, width = 100,
  showCenter = false, centerDotSize = 2.4,
  centerDotColor = "#e22020",
}) {
  const L = layout;
  const ppmm = width / L.PAGE_W;
  const height = L.PAGE_H * ppmm;

  return (
    <div
      className="relative overflow-hidden rounded-[3px] bg-white"
      style={{ width, height }}
    >
      {page.cells.map((cell, i) => {
        const { x, y } = cellCenterMM(i, L);
        const outerPx = L.OUTER_D * ppmm;
        const innerPx = L.INNER_D * ppmm;
        const left = (x - L.OUTER_D / 2) * ppmm;
        const top = (y - L.OUTER_D / 2) * ppmm;

        return (
          <div
            key={i}
            className="absolute"
            style={{ left, top, width: outerPx, height: outerPx }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: `${Math.max(0.5, 0.15 * ppmm)}px solid rgba(30,30,30,0.55)`,
              }}
            />

            <div
              className="absolute overflow-hidden rounded-full"
              style={{
                left: (outerPx - innerPx) / 2,
                top: (outerPx - innerPx) / 2,
                width: innerPx,
                height: innerPx,
                border: `${Math.max(0.4, 0.08 * ppmm)}px solid rgba(120,130,150,0.5)`,
                background:
                  cell.type === "color"
                    ? (cell.fillColor || "#cccccc")
                    : cell.type === "image"
                    ? (cell.bgColor || "#ffffff")
                    : "#ffffff",
              }}
            >
              {cell.type === "image" && (
                <MiniImage cell={cell} projectId={projectId} />
              )}

              {cell.type === "text" && cell.text && (
                <div className="flex h-full w-full items-center justify-center">
                  <span
                    style={{
                      fontSize: fitFontPx(cell.text, innerPx * 0.82, {
                        fontFamily: cell.fontFamily,
                        bold: cell.bold,
                      }),
                      fontFamily: CSS_FONT[cell.fontFamily] || CSS_FONT.sans,
                      fontWeight: cell.bold ? 700 : 400,
                      color: cell.color || "#111111",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {cell.text}
                  </span>
                </div>
              )}
            </div>

            {showCenter && (() => {
              const midRpx = ((L.INNER_D / 2 + L.OUTER_D / 2) / 2) * ppmm;
              const dot = Math.max(1.2, (centerDotSize || 2.4) * ppmm);

              return (
                <div
                  className="pointer-events-none absolute rounded-full"
                  style={{
                    left: outerPx / 2 - dot / 2,
                    top: outerPx / 2 - midRpx - dot / 2,
                    width: dot,
                    height: dot,
                    background: centerDotColor || "#e22020",
                  }}
                />
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

export const MiniPage = memo(MiniPageImpl);
